import {
  Calendar,
  Clock,
  Home,
  Minus,
  Plus,
  ShoppingCart,
  TrendingUp,
  X,
} from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import BaseUrl from "../../../constants/BaseUrl";
import * as AuthAction from "../../../store/actions/auth/AuthAction";
import { ShowError, ShowSuccess } from "../../../utils/ToasFunction";
import MyLoader from "../../reusebale/myLoader";
import ParagraphTag from "../../reusebale/Paragraph";

const AddProperty = () => {
  const { loginSuccess, userCompleteProfile } = useSelector(
    (state) => state.auth
  );
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [additionalProperties, setAdditionalProperties] = useState(1);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [mainPlan, setMainPlan] = useState(null);

  // Fetch active subscription from landlordsubscriptions (source of truth)
  useEffect(() => {
    if (!loginSuccess?.user?._id || !loginSuccess?.token) return;

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${loginSuccess.token}`);

    fetch(
      `${BaseUrl}landlord/profile/getAllSubscriptions/${loginSuccess.user._id}`,
      { method: "GET", headers: myHeaders, redirect: "follow" }
    )
      .then((res) => res.json())
      .then((result) => {
        if (result.status === "Success" && result.data) {
          const activeSub = result.data.find(
            (s) => s.status === "active" && !s.subscriptionPlanName?.includes("Add-on")
          );
          if (activeSub) {
            setMainPlan({
              endDate: activeSub.endDate,
              startDate: activeSub.startDate,
              packageData: activeSub.packageData || {},
            });
          }
        }
      })
      .catch((err) => console.error("Error fetching subscriptions:", err));
  }, [loginSuccess?.user?._id, loginSuccess?.token]);

  const calculateAdditionalPropertyPrice = (propertyCount) => {
    if (!propertyCount || propertyCount <= 0 || !mainPlan) {
      return {
        totalPrice: "$0",
        monthlyPrice: "$0",
        discountPercentage: 0,
        discountAmount: "$0",
        originalPrice: "$0",
        finalTotalPrice: 0,
        finalMonthlyPrice: 0,
        remainingMonths: 0,
        pricePerMonth: 0,
      };
    }

    const pricePerProperty = mainPlan.packageData?.pricePerProperty || 0;
    const totalMonths = mainPlan.packageData?.numberOfMonth || 6;

    const currentDate = moment();
    const endDate = moment(mainPlan.endDate);
    const remainingMonths = Math.max(
      1,
      Math.ceil(endDate.diff(currentDate, "months", true))
    );

    const pricePerMonthPerProperty = pricePerProperty / totalMonths;

    const originalPrice =
      propertyCount * pricePerMonthPerProperty * remainingMonths;

    let discountPercentage = 0;
    if (propertyCount >= 25) {
      discountPercentage = 20;
    } else if (propertyCount >= 10) {
      discountPercentage = 10;
    }

    const discountAmount = Math.round(
      (originalPrice * discountPercentage) / 100
    );
    const totalPrice = originalPrice - discountAmount;
    const monthlyPrice = Math.round(totalPrice / remainingMonths);

    return {
      totalPrice: `$${Math.round(totalPrice).toLocaleString()}`,
      monthlyPrice: `$${monthlyPrice.toLocaleString()}`,
      discountPercentage,
      discountAmount: `$${Math.round(discountAmount).toLocaleString()}`,
      originalPrice: `$${Math.round(originalPrice).toLocaleString()}`,
      finalTotalPrice: Math.round(totalPrice),
      finalMonthlyPrice: monthlyPrice,
      remainingMonths,
      pricePerMonth: Math.round(pricePerMonthPerProperty),
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const buyAdditionalProperties = async () => {
    if (!mainPlan || additionalProperties <= 0) {
      ShowError("Please enter a valid number of properties");
      return;
    }

    setPurchaseLoading(true);
    const pricing = calculateAdditionalPropertyPrice(additionalProperties);

    const PropertyAddOn = {
      packageId: mainPlan.packageData._id,
      startDate: new Date().toISOString(),
      endDate: mainPlan.endDate,
      packageData: {
        _id: mainPlan.packageData._id,
        planName: `${mainPlan.packageData.planName} - Add-on`,
        numberOfMonth: pricing.remainingMonths,
        totalPackagePrice: pricing.finalTotalPrice,
        monthlyPrice: pricing.finalMonthlyPrice,
        pricePerProperty: pricing.pricePerMonth,
        isAnnual: mainPlan.packageData.isAnnual,
        isCustom: true,
        allowProperties: additionalProperties,
        discount: pricing.discountPercentage,
        customPropertyCount: additionalProperties,
        discountPercentage: pricing.discountPercentage,
        discountAmount: pricing.discountAmount,
        originalPrice: pricing.originalPrice,
        remainingMonths: pricing.remainingMonths,
        originalPricePerProperty: mainPlan.packageData.pricePerProperty,
        calculatedMonthlyRate: pricing.pricePerMonth,
      },
    };

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    try {
      const response = await fetch(
        `${BaseUrl}landlord/profile/addPropertySubscription`,
        {
          method: "POST",
          headers: myHeaders,
          body: JSON.stringify({
            landloardId: loginSuccess?.user?._id,
            PropertyAddOn,
          }),
          redirect: "follow",
        }
      );
      const result = await response.json();

      if (result.status === "Success") {
        ShowSuccess(
          `Successfully added ${additionalProperties} additional ${additionalProperties === 1 ? "property" : "properties"}!`
        );
        dispatch(
          AuthAction.GetLandlordProfile(
            loginSuccess?.user?._id,
            loginSuccess.token
          )
        );
        setAdditionalProperties(1);
        setShowModal(false);
      } else {
        ShowError(result.message || "Failed to add additional properties");
      }
    } catch (error) {
      console.error("Error:", error);
      ShowError("Failed to add additional properties");
    } finally {
      setPurchaseLoading(false);
    }
  };

  if (!mainPlan) return null;

  const pricing = calculateAdditionalPropertyPrice(additionalProperties);

  const increment = () => setAdditionalProperties((prev) => prev + 1);
  const decrement = () =>
    setAdditionalProperties((prev) => Math.max(1, prev - 1));

  const closeModal = () => {
    setShowModal(false);
    setAdditionalProperties(1);
  };

  return (
    <>
      {/* Trigger Button */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center space-x-3 px-6 py-3 bg-orange-500 text-white rounded-lg font-popinsSemiBold text-lg hover:bg-orange-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          <ShoppingCart size={20} />
          <span>Buy Additional Properties</span>
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <main className="w-full h-screen backdrop-blur-sm bg-black/80 fixed inset-0 z-50 flex items-center justify-center">
          <section className="w-[90%] sm:w-[75%] md:w-[60%] lg:w-[50%] xl:w-[40%] bg-white shadow rounded-[31px] mx-auto p-6 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="bg-orange-500 p-2 rounded-md">
                  <Plus className="text-white" size={20} />
                </div>
                <ParagraphTag
                  content="Add More Properties"
                  classes="text-darkColor font-popinsSemiBold text-lg"
                />
              </div>
              <button onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            {/* Property Count with +/- Buttons */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
              <ParagraphTag
                content="Number of Additional Properties"
                classes="text-darkColor font-popinsSemiBold text-sm mb-2"
              />
              <div className="flex items-center space-x-3 mt-1">
                <button
                  onClick={decrement}
                  className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-themeColor text-themeColor hover:bg-themeColor hover:text-white transition-colors"
                >
                  <Minus size={16} strokeWidth={3} />
                </button>
                <span className="text-darkColor font-popinsSemiBold text-2xl w-12 text-center">
                  {additionalProperties}
                </span>
                <button
                  onClick={increment}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-themeColor text-white hover:bg-themeColor/90 transition-colors"
                >
                  <Plus size={16} strokeWidth={3} />
                </button>
              </div>
            </div>

            {/* Pricing Info Box */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <div className="flex items-start space-x-2">
                <div className="bg-blue-100 p-1 rounded-full mt-1">
                  <Calendar className="text-blue-600" size={14} />
                </div>
                <div>
                  <ParagraphTag
                    content="Smart Pricing"
                    classes="text-blue-800 font-popinsSemiBold text-sm mb-1"
                  />
                  <ParagraphTag
                    content={`You'll only pay for the remaining ${pricing.remainingMonths} months of your subscription. We calculate the monthly rate from your original package and charge you proportionally.`}
                    classes="text-blue-700 font-popinsMedium text-xs leading-relaxed"
                  />
                </div>
              </div>
            </div>

            {/* Price Calculation Display */}
            <div className="bg-white p-4 rounded-lg border border-orange-300 shadow-sm mb-4">
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="text-orange-600" size={18} />
                <ParagraphTag
                  content="Pricing Breakdown"
                  classes="text-darkColor font-popinsSemiBold text-lg"
                />
              </div>

              {/* Pricing Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Properties Info */}
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Home className="text-blue-600" size={16} />
                    <ParagraphTag
                      content="Properties"
                      classes="text-blue-700 font-popinsMedium text-sm"
                    />
                  </div>
                  <ParagraphTag
                    content={`${additionalProperties} Properties`}
                    classes="text-blue-800 font-popinsBold text-xl"
                  />
                  <ParagraphTag
                    content={`${formatCurrency(pricing.pricePerMonth)} per month each`}
                    classes="text-blue-600 font-popinsMedium text-xs"
                  />
                </div>

                {/* Duration Info */}
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="text-green-600" size={16} />
                    <ParagraphTag
                      content="Remaining Duration"
                      classes="text-green-700 font-popinsMedium text-sm"
                    />
                  </div>
                  <ParagraphTag
                    content={`${pricing.remainingMonths} Months`}
                    classes="text-green-800 font-popinsBold text-xl"
                  />
                  <ParagraphTag
                    content={`Until ${moment(mainPlan.endDate).format("MMM DD, YYYY")}`}
                    classes="text-green-600 font-popinsMedium text-xs"
                  />
                </div>
              </div>

              {/* Detailed Calculation */}
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-darkColor/60 font-popinsMedium">
                    Monthly Rate per Property:
                  </span>
                  <span className="text-darkColor font-popinsMedium">
                    {formatCurrency(pricing.pricePerMonth)}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-darkColor/60 font-popinsMedium">
                    Base Calculation:
                  </span>
                  <span className="text-darkColor font-popinsMedium">
                    {additionalProperties} × {formatCurrency(pricing.pricePerMonth)} ×{" "}
                    {pricing.remainingMonths} months
                  </span>
                </div>

                {pricing.discountPercentage > 0 && (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-darkColor/60 font-popinsMedium">
                        Original Price:
                      </span>
                      <span className="text-darkColor font-popinsMedium line-through">
                        {pricing.originalPrice}
                      </span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-green-600 font-popinsMedium">
                        Volume Discount ({pricing.discountPercentage}%):
                      </span>
                      <span className="text-green-600 font-popinsMedium">
                        -{pricing.discountAmount}
                      </span>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center py-3 bg-orange-50 px-4 rounded-lg border border-orange-200">
                  <span className="text-orange-800 font-popinsBold text-lg">
                    Total Price:
                  </span>
                  <span className="text-orange-600 font-popinsBold text-2xl">
                    {pricing.totalPrice}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2">
                  <span className="text-darkColor/60 font-popinsMedium">
                    Average Monthly Cost:
                  </span>
                  <span className="text-darkColor font-popinsSemiBold text-lg">
                    {pricing.monthlyPrice}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2">
                  <span className="text-darkColor/60 font-popinsMedium">
                    Valid Until:
                  </span>
                  <span className="text-darkColor font-popinsMedium">
                    <Calendar className="inline mr-1" size={14} />
                    {moment(mainPlan.endDate).format("MMM DD, YYYY")}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              {purchaseLoading ? (
                <div className="flex justify-center items-center w-full py-3">
                  <MyLoader />
                  <span className="ml-2 text-darkColor font-popinsMedium">
                    Processing...
                  </span>
                </div>
              ) : (
                <>
                  <button
                    onClick={closeModal}
                    className="flex-1 py-3 rounded-lg border-2 border-gray-300 text-gray-600 font-popinsMedium text-sm hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={buyAdditionalProperties}
                    className="flex-1 py-3 rounded-lg bg-themeColor text-white font-popinsSemiBold text-sm hover:bg-themeColor/90 transition-colors shadow-lg"
                  >
                    Confirm & Pay {pricing.totalPrice}
                  </button>
                </>
              )}
            </div>
          </section>
        </main>
      )}
    </>
  );
};

export default AddProperty;
